import { useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Hero1 from "@stackje/shared-components/cards/hero/Hero1";
import Hero2 from "@stackje/shared-components/cards/hero/Hero2";
import Hero3 from "@stackje/shared-components/cards/hero/Hero3";
import Hero4 from "@stackje/shared-components/cards/hero/Hero4";
import Hero5 from "@stackje/shared-components/cards/hero/Hero5";
import { ClearableInput } from "@stackje/shared-components/ui/clearable-fields";
import { SimpleMarkdownEditor } from "@stackje/shared-components/ui/simple-markdown-editor";
import { Label } from "@stackje/shared-components/ui/label";
import type { SelectedImageData } from "@/components/shared/ImageEditor";
import { FormImageUploader } from "@/components/shared/FormImageUploader";
import { useColorSchemeShuffler } from "../../../hooks/useColorSchemeShuffler";
import type { CommonColor, Hero1Card, SocialMediaLink } from "@stackje/common/config";
import { SocialMediaPlatformSchema } from "@stackje/common/config";
import { sampleData } from "@/components/add-card/constants";
import { SocialLinksField } from "./social-links-field";

// Zod schema for validation
const heroCardFormSchema = z.object({
  brandTitle: z.string().optional(),
  title: z.string().optional(),
  description: z.string().optional(),
  buttonText: z.string().optional(),
  buttonUrl: z.string().url().optional().or(z.literal("")),
  socialLinks: z.array(z.object({
    platform: SocialMediaPlatformSchema,
    url: z.string(),
    displayName: z.string().optional(),
  })).optional(),
  type: z.enum(["hero1", "hero2", "hero3", "hero4", "hero5"]),
  logo: z.string().optional(),
  image: z.string().optional(),
  backgroundImage: z.string().optional(),
  colors: z.object({
    background: z.string(),
    foreground: z.string(),
    cardBackground: z.string(),
    cardForeground: z.string(),
    buttonBackground: z.string(),
    buttonForeground: z.string(),
  }),
});

export type HeroCardFormData = z.infer<typeof heroCardFormSchema>;

export interface HeroCardData {  
  brandTitle: string;
  title: string;
  description: string;
  button?: {
    title: string;
    url: string;
  };
  socialLinks: SocialMediaLink[];
  type: "hero1" | "hero2" | "hero3" | "hero4" | "hero5";
  logo?: string;
  image?: string;
  backgroundImage?: string;
  colors: CommonColor;
}

interface HeroCardFormProps {
  initialData: HeroCardFormData;
  mode: "add" | "edit";
  onTypeChange?: (type: "hero1" | "hero2" | "hero3" | "hero4" | "hero5") => void;
}

export function useHeroCardForm({
  initialData,
  mode,
  onTypeChange,
}: HeroCardFormProps) {
  const form = useForm<HeroCardFormData>({
    resolver: zodResolver(heroCardFormSchema),
    defaultValues: {
      ...initialData,
      socialLinks: initialData.socialLinks || [],
      logo: initialData.logo || "",
      image: initialData.image || "",
      backgroundImage: initialData.backgroundImage || "",
    },
  });

  const [logo, setLogo] = useState<SelectedImageData | null>(
    initialData.logo ? {
      fullUrl: initialData.logo,
      previewUrl: initialData.logo,
    } : null,
  );

  const [image, setImage] = useState<SelectedImageData | null>(
    initialData.image ? {
      fullUrl: initialData.image,
      previewUrl: initialData.image,
    } : null,
  );

  const [backgroundImage, setBackgroundImage] = useState<SelectedImageData | null>(
    initialData.backgroundImage ? {
      fullUrl: initialData.backgroundImage,
      previewUrl: initialData.backgroundImage,
    } : null,
  );
  
  const { colors, setColors, shuffleColors } = useColorSchemeShuffler(initialData.colors);

  // Watch form values for preview updates
  const watchedValues = form.watch();
  const brandTitle = watchedValues.brandTitle || "";
  const title = watchedValues.title || "";
  const description = watchedValues.description || "";
  const buttonText = watchedValues.buttonText || "";
  const buttonUrl = watchedValues.buttonUrl || "";
  const socialLinks = watchedValues.socialLinks || [];
  const type = watchedValues.type;
  const isDirty = form.formState.isDirty;

  useEffect(() => {
    setColors(initialData.colors);
    form.setValue("colors", initialData.colors);
  }, [initialData.colors, setColors, form]);

  // Update form colors when shuffled
  useEffect(() => {
    form.setValue("colors", colors);
  }, [colors, form]);

  // Update form values when images change
  useEffect(() => {
    form.setValue("logo", logo?.fullUrl || "");
  }, [logo, form]);

  useEffect(() => {
    form.setValue("image", image?.fullUrl || "");
  }, [image, form]);

  useEffect(() => {
    form.setValue("backgroundImage", backgroundImage?.fullUrl || "");
  }, [backgroundImage, form]);

  const toggleType = (newType?: string) => {
    if (newType) {
      form.setValue("type", newType as "hero1" | "hero2" | "hero3" | "hero4" | "hero5");
      onTypeChange?.(newType as "hero1" | "hero2" | "hero3" | "hero4" | "hero5");
      return;
    }

    const types = ["hero1", "hero2", "hero3", "hero4", "hero5"] as const;
    const currentIndex = types.indexOf(type);
    const nextIndex = (currentIndex + 1) % types.length;
    const nextType = types[nextIndex];
    form.setValue("type", nextType);
    onTypeChange?.(nextType);
  };

  const formContent = (
    <>
      <div className="space-y-2 mt-2">
        <div className="py-2">
          <h2 className="text-lg font-semibold">Hero Card</h2>
          <p className="text-sm text-muted-foreground">
            Not all fields will be used for a given card type.
          </p>
          <p className="text-sm text-muted-foreground">
            <span className="text-red-500">*</span> indicates required fields
          </p>
        </div>

        <div className="border-t pb-2 pt-4">
          <Label htmlFor="brandTitle">Brand Title</Label>
          <Controller
            name="brandTitle"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="brandTitle"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Your Brand Name"
              />
            )}
          />
        </div>

        <div className="py-2">
          <FormImageUploader
            label="Brand Logo"
            currentImage={logo}
            onImageChange={setLogo}
            targetCropWidth={128}
            targetCropHeight={128}
            allowFreeCrop={false}
            imagePreviewClassName="max-w-[128px] h-auto rounded border object-contain aspect-square bg-muted"
          />
        </div>
      </div>

      <div className="border-t py-4">
        <div className="my-2">
          <Label htmlFor="title">Main Heading</Label>
          <Controller
            name="title"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="title"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Welcome to Our Site"
              />
            )}
          />
        </div>
        <div className="my-2">
          <Label htmlFor="description">Subheading</Label>
          <Controller
            name="description"
            control={form.control}
            render={({ field }) => (
              <SimpleMarkdownEditor
                id="description"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Your one-stop solution for all needs"
              />
            )}
          />
        </div>
      </div>

      <div className="border-t py-4">
        <div className="my-2">
          <Label htmlFor="buttonText">Button Text</Label>
          <Controller
            name="buttonText"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="buttonText"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="Get Started"
              />
            )}
          />
        </div>
        <div className="my-2">
          <Label htmlFor="buttonUrl">Button URL</Label>
          <Controller
            name="buttonUrl"
            control={form.control}
            render={({ field }) => (
              <ClearableInput
                id="buttonUrl"
                value={field.value || ""}
                onChange={field.onChange}
                placeholder="https://example.com"
              />
            )}
          />
        </div>
      </div>

      {/* Social Links Section */}
      <SocialLinksField
        control={form.control}
        name="socialLinks"
      />

      <div className="border-t py-4">
        <FormImageUploader
          label="Profile/Hero Image"
          currentImage={image}
          onImageChange={setImage}
          allowFreeCrop={true}
        />
      </div>

      {/* Background Image - Always show for all Hero types */}
      <div className="border-t py-4">
        <FormImageUploader
          label="Background Image"
          currentImage={backgroundImage}
          onImageChange={setBackgroundImage}
          allowFreeCrop={true}
        />
      </div>

      {/* Hidden inputs for form submission */}
      <input type="hidden" name="type" value={type} />
      <input type="hidden" name="colors" value={JSON.stringify(colors)} />
      <input type="hidden" name="logo" value={logo?.fullUrl || ""} />
      <input type="hidden" name="image" value={image?.fullUrl || ""} />
      <input type="hidden" name="backgroundImage" value={backgroundImage?.fullUrl || ""} />
      
      {/* Social links as JSON (primary method) */}
      <input type="hidden" name="socialLinks" value={JSON.stringify(socialLinks)} />
      
      {/* Social links as individual fields (fallback method) */}
      {socialLinks.map((socialLink, index) => (
        <div key={index}>
          <input
            type="hidden"
            name={`socialPlatform${index}`}
            value={socialLink.platform}
          />
          <input 
            type="hidden" 
            name={`socialUrl${index}`} 
            value={socialLink.url} 
          />
          <input
            type="hidden"
            name={`socialDisplayName${index}`}
            value={socialLink.displayName || ""}
          />
        </div>
      ))}
    </>
  );

  const previewContent = isDirty || mode === "edit" ? (() => {
    // Common props for all hero types since they now have unified schema
    const commonHeroProps = {
      brandTitle,
      title,
      description,
      button: buttonUrl && buttonText 
        ? { title: buttonText, url: buttonUrl }
        : undefined,
      socialLinks,
      logo: logo?.fullUrl ? { src: logo.fullUrl, alt: brandTitle || "Brand logo" } : undefined,
      image: image?.fullUrl ? { src: image.fullUrl, alt: title || "Hero image" } : undefined,
      backgroundImage: backgroundImage?.fullUrl ? { src: backgroundImage.fullUrl, alt: "Background" } : undefined,
      diagonalAngle: 20, // Default angle for Hero5
      colors: colors as Hero1Card["colors"], // All hero types have same color schema now
    };

    if (type === "hero1") {
      return <Hero1 {...commonHeroProps} />;
    } else if (type === "hero2") {
      return <Hero2 {...commonHeroProps} />;
    } else if (type === "hero3") {
      return <Hero3 {...commonHeroProps} />;
    } else if (type === "hero4") {
      return <Hero4 {...commonHeroProps} />;
    } else {
      return <Hero5 {...commonHeroProps} />;
    }
  })() : (() => {
    // Get sample data for current hero type - safely cast to hero-specific sample
    const heroSampleData = {
      hero1: sampleData.hero1,
      hero2: sampleData.hero2, 
      hero3: sampleData.hero3,
      hero4: sampleData.hero4,
      hero5: sampleData.hero5,
    };
    
    const currentSample = heroSampleData[type];
    
    // Common props for sample data preview
    const sampleHeroProps = {
      brandTitle: currentSample?.brandTitle || "Sample Brand",
      title: currentSample?.title || "Sample Title", 
      description: currentSample?.description || "Sample description",
      button: currentSample?.button,
      socialLinks: currentSample?.socialLinks || [],
      logo: currentSample?.logo,
      image: { src: "/api/placeholder/120/120", alt: "Profile" },
      backgroundImage: { src: "/api/placeholder/120/120", alt: "Background" },
      diagonalAngle: 20,
      colors: colors as Hero1Card["colors"], // All hero types have same color schema now
    };

    if (type === "hero1") {
      return <Hero1 {...sampleHeroProps} />;
    } else if (type === "hero2") {
      return <Hero2 {...sampleHeroProps} />;
    } else if (type === "hero3") {
      return <Hero3 {...sampleHeroProps} />;
    } else if (type === "hero4") {
      return <Hero4 {...sampleHeroProps} />;
    } else {
      return <Hero5 {...sampleHeroProps} />;
    }
  })();

  return {
    form,
    brandTitle,
    title,
    description,
    buttonText,
    buttonUrl,
    socialLinks,
    type,
    toggleType,
    shuffleColors,
    isDirty,
    formContent,
    previewContent,
    handleSubmit: form.handleSubmit,
  };
}
